import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Video, Phone, MapPin, CalendarPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/business/PageHeader";
import { format } from "date-fns";

const SLOTS = ["10:00-10:30", "10:30-11:00", "11:00-11:30", "12:00-12:30", "15:00-15:30", "16:00-16:30", "17:00-17:30", "18:00-18:30"];
const TOPICS = ["ITR filing help", "Old vs new regime review", "Capital gains advice", "Business / freelance tax", "Notice received", "Tax planning"];

export default function Appointments() {
  const [appts, setAppts] = useState([]);
  const [cas, setCas] = useState([]);
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(new Date());
  const [slot, setSlot] = useState(SLOTS[0]);
  const [topic, setTopic] = useState(TOPICS[0]);
  const [mode, setMode] = useState("video");
  const [caId, setCaId] = useState("");

  const load = () => api.get("/appointments").then(({ data }) => setAppts(data));
  useEffect(() => { load(); api.get("/cas").then(({ data }) => { setCas(data); if (data[0]) setCaId(data[0].id); }); }, []);

  const book = async () => {
    try {
      await api.post("/appointments", { ca_id: caId || null, date: date.toISOString(), slot, topic, mode });
      toast.success("Appointment booked");
      setOpen(false); load();
    } catch { toast.error("Could not book"); }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto">
        <PageHeader>
          <PageHeader.Title>Book a CA Consultation</PageHeader.Title>
          <PageHeader.Actions>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="book-trigger"><CalendarPlus className="h-4 w-4 mr-2" /> New booking</Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Book a session</DialogTitle></DialogHeader>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label className="text-xs mb-2 block">Pick a date</Label>
                  <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} className="rounded-md border border-border p-2" />
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs">Chartered Accountant</Label>
                    <Select value={caId} onValueChange={setCaId}>
                      <SelectTrigger className="h-11 mt-1.5"><SelectValue placeholder="Auto-assign" /></SelectTrigger>
                      <SelectContent>
                        {cas.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Time slot</Label>
                    <Select value={slot} onValueChange={setSlot}>
                      <SelectTrigger className="h-11 mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{SLOTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Topic</Label>
                    <Select value={topic} onValueChange={setTopic}>
                      <SelectTrigger className="h-11 mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{TOPICS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Mode</Label>
                    <Select value={mode} onValueChange={setMode}>
                      <SelectTrigger className="h-11 mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="video">Video (Google Meet)</SelectItem>
                        <SelectItem value="phone">Phone call</SelectItem>
                        <SelectItem value="in_person">In-person (Bengaluru office)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={book} className="w-full" data-testid="book-confirm">Confirm booking</Button>
                </div>
              </div>
            </DialogContent>
            </Dialog>
          </PageHeader.Actions>
        </PageHeader>
        <p className="mt-2 text-sm text-muted-foreground">Video, phone or in-person. Choose a slot that works for you.</p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="appts-list">
          {appts.length === 0 && <Card className="p-8 border-border text-center text-sm text-muted-foreground">No appointments yet. Book your first consultation.</Card>}
          {appts.map(a => (
            <Card key={a.id} className="p-5 border-border">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{format(new Date(a.date), "EEEE, d MMMM")}</div>
                  <div className="mt-2 font-display text-lg font-semibold">{a.topic}</div>
                  <div className="mt-1 text-sm text-muted-foreground">{a.slot} · <span className="capitalize">{a.mode.replace("_", " ")}</span></div>
                </div>
                <div className="capitalize text-xs font-semibold rounded px-2 py-0.5 bg-muted">{a.status}</div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                {a.mode === "video" && a.meet_link && (
                  <Button asChild size="sm" variant="outline"><a href={a.meet_link} target="_blank" rel="noreferrer"><Video className="h-3.5 w-3.5 mr-1.5" /> Join</a></Button>
                )}
                <Button onClick={() => api.delete(`/appointments/${a.id}`).then(() => { load(); toast.success("Cancelled"); })} variant="ghost" size="sm">
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Cancel
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
